import NextLink from 'next/link';

import { Box, Flex, Heading, Link, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { ChevronRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

import {
  RPL_ANALYTICS_LABEL,
  RPL_COMPACT_QUOTES,
  RPL_EXPLORE_MASTERS_LABEL,
  RPL_MASTERS_PATH,
  RPL_PROMO_BODY,
  RPL_PROMO_HEADLINE,
} from '../constants';

import { RplCalculateButton } from './rpl-calculate-button';
import { RplQuoteCard } from './rpl-quote-card';
import { RplStarEyebrow } from './rpl-star-eyebrow';

export function RplHomepageSection({ eventName = 'Homepage CTA Clicked' }: { eventName?: string } = {}) {
  return (
    <Box
      aria-label='Recognition of prior learning'
      as='section'
      py={{ base: '48px', md: '72px' }}
      bgGradient={{
        base: 'linear(180deg, #071C4E 0%, #060A12 100%)',
        md: 'linear(90deg, #071C4E 0%, #060A12 100%)',
      }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: '32px', lg: '56px' }}>
          <Flex direction='column' flex={1} gap='16px'>
            <RplStarEyebrow />
            <Heading as='h2' color='white' fontWeight='600' size={{ base: 'heading-7', md: 'heading-6' }}>
              {RPL_PROMO_HEADLINE}
            </Heading>
            <Text color='grey.300'>{RPL_PROMO_BODY}</Text>
            <Flex
              alignItems={{ base: 'stretch', md: 'center' }}
              direction={{ base: 'column', md: 'row' }}
              gap='20px'
              mt='8px'
            >
              <RplCalculateButton eventName={eventName} width={{ base: '100%', md: 'fit-content' }} />
              <ExploreMastersLink eventName={eventName} />
            </Flex>
          </Flex>
          <Box alignSelf='stretch' bg='gold.300' display={{ base: 'none', lg: 'block' }} opacity={0.7} w='2px' />
          <Box bg='gold.300' display={{ base: 'block', lg: 'none' }} h='2px' opacity={0.7} />
          <Flex direction='column' flex={1} gap='28px'>
            {RPL_COMPACT_QUOTES.map((quote) => (
              <RplQuoteCard key={quote.name} quote={quote} tone='dark' />
            ))}
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function ExploreMastersLink({ eventName }: { eventName: string }) {
  return (
    <Link
      _active={{ color: 'gold.300' }}
      _hover={{ color: 'gold.100' }}
      _visited={{ color: 'gold.300' }}
      alignItems='center'
      as={NextLink}
      color='gold.300'
      display='inline-flex'
      fontSize='sm'
      fontWeight='500'
      gap='4px'
      href={RPL_MASTERS_PATH}
      justifyContent={{ base: 'center', md: 'flex-start' }}
      onClick={() => {
        trackEvent({
          label: RPL_ANALYTICS_LABEL.exploreMasters,
          name: eventName,
          type: 'click',
        });
      }}
    >
      {RPL_EXPLORE_MASTERS_LABEL}
      <ChevronRightIcon height='12px' width='12px' />
    </Link>
  );
}
